// --------------------------------------------------------------
// Created On: 2025-05-06
// Author: Hannah Vaughan
//
// Last Modified: 2026-07-31
// Modified By: Zachary Thomas
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useMemo } from "react";
import styles from "./AnalogInputPage.module.scss";
import IconTooltip from "src/components/IconTooltip/IconTooltip";
import { ANALOG_INPUTS } from "src/constants/attributes";
import AnalogSensor from "./AnalogInput/AnalogInput";
import WarningList from "src/components/WarningList/WarningList";

// Page of the Asset Management Wizard that allows the user to optionally add analog sensor info.
export default function AnalogInputPage(props: Props): Component {
  const sensorPositions = useMemo(() => getSensorPositions(props.sensorCount), [props.sensorCount]);
  const localSensorPositionsInUse = useMemo(
    () => getLocalSensorPositionsInUse(props.analogInputCards),
    [JSON.stringify(props.analogInputCards)]
  );
  const [mainWarningMessage, warningMessages] = useMemo(
    () => getWarningMessages(props.analogInputsInUse),
    [JSON.stringify(props.analogInputsInUse)]
  );

  // Generates an array of numbers from 1 to X, where X is the max sensor count for analog sensors.
  function getSensorPositions(sensorCount: number): number[] {
    return new Array<number>(sensorCount).fill(0).map((_, i) => i + 1);
  }

  // Gathers a list of all analog sensor positions that are in use by the current asset.
  function getLocalSensorPositionsInUse(analogInputCards: AnalogInputCard[]): number[] {
    const newLocalSensorPositionsInUse: number[] = [];
    analogInputCards.forEach((card) => {
      newLocalSensorPositionsInUse.push(card.sensorNumber);
    });
    return newLocalSensorPositionsInUse;
  }

  // Populates the list of warnings whenever there are any analog sensors in use by other assets in the network.
  function getWarningMessages(analogInputsInUse: SensorInfoMap | null): [string, string[]] {
    let newMainWarningMessage = "";
    const newWarningMessages: string[] = [];
    if (analogInputsInUse !== null && Object.keys(analogInputsInUse).length > 0) {
      newMainWarningMessage = "The following analog input sensors are already in use:";
      Object.entries(analogInputsInUse).forEach(([sensorPosition, sensor]) => {
        const formattedSensor: SensorInfo = sensor as SensorInfo;
        newWarningMessages.push(
          `Position #${sensorPosition} is already reporting '${formattedSensor.reporting}' ` +
            `for asset '${formattedSensor.assetName}'.`
        );
      });
    }
    return [newMainWarningMessage, newWarningMessages];
  }

  return (
    <div data-test="analog-sensors-page">
      <div className="mx-1 my-2">
        <label className={styles.title}>
          <span className="ms-3 me-2">Analog Input Sensors</span>
          <IconTooltip
            id="analog-sensors-tooltip"
            icon="info-circle"
            message={
              "Describes the specific type of analog input sensors connected to the monitoring device and " +
              "the type of data that each reports."
            }
            color="var(--info-tooltip)"
          />
        </label>
        <button
          data-test="add-analog-sensor-button"
          type="submit"
          className={`${styles.addButton} btn btn-success float-end`}
          onClick={() => props.onCreate()}
          disabled={
            props.analogInputCards.length >= props.sensorCount ||
            (props.analogInputsInUse !== null &&
              props.analogInputsInUse !== undefined &&
              Object.keys(props.analogInputsInUse).length >= props.sensorCount) ||
            props.disabled
          }
        >
          <span className="d-none d-sm-inline">Add Analog Input</span>
          <i className="d-inline d-sm-none fa fa-fw fa-plus fa-xs" />
        </button>
      </div>
      {props.analogInputCards.map((card, i) => (
        <AnalogSensor
          key={card.formId}
          formIndex={i}
          formId={card.formId}
          sensorNumber={card.sensorNumber}
          sourceAttribute={card.sensorNumber > 0 ? ANALOG_INPUTS[card.sensorNumber - 1].attribute : ""}
          sensorPositions={sensorPositions}
          analogSensors={props.analogSensors}
          analogSensorMap={props.analogSensorMap}
          analogInputsInUse={props.analogInputsInUse}
          localSensorPositionsInUse={localSensorPositionsInUse}
          disabled={props.disabled}
          onCreate={() => props.onCreate()}
          onChange={(formId, sensorNumber, analogSensorId, attributeId, attributeCode) =>
            props.onChange(formId, sensorNumber, analogSensorId, attributeId, attributeCode)
          }
          onDelete={(formId, sensorNumber, attributeCode) => props.onDelete(formId, sensorNumber, attributeCode)}
        />
      ))}
      {mainWarningMessage.length > 0 && warningMessages.length > 0 && (
        <div className="m-3">
          <WarningList mainMessage={mainWarningMessage} messages={warningMessages} />
        </div>
      )}
    </div>
  );
}

interface Props {
  sensorCount: number;
  analogSensorMap: AnalogSensorMap;
  analogSensors: AnalogSensor[];
  analogInputCards: AnalogInputCard[];
  analogInputsInUse: SensorInfoMap | null;
  disabled: boolean;
  onCreate: () => void;
  onChange: (
    formId: number,
    sensorNumber: number,
    analogSensorId: number | null,
    attributeId: number | null,
    attributeCode: string
  ) => void;
  onDelete: (formId: number, sensorNumber: number, attributeCode: string) => void;
}

interface AnalogInputCard {
  formId: number;
  sensorNumber: number;
  analogSensorId: number | null;
  attributeId: number | null;
  attributeCode: string;
}

interface AnalogSensor {
  analogSensorId: number;
  name: string;
  applications: AnalogApplication[];
}

interface AnalogApplication {
  attributeId: number;
  attributeConnectedId: number;
  name: string;
}

interface AnalogSensorMap {
  [key: string]: AnalogInputSelection;
}

interface AnalogInputSelection {
  analogSensorId: number;
  attributeCode: string;
  attributeConnectedCode: string;
  attributeModeCode: string;
  attributeMinCode: string;
  attributeSpanCode: string;
  mappedAttributeId: number;
  mappedAttributeConnectedId: number;
}

interface SensorInfoMap {
  [sensorNumber: number]: SensorInfo;
}

interface SensorInfo {
  reporting: string;
  assetName: string;
}
